import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Incident } from "@/types/incidents";
import { useIncidents } from "@/context/IncidentsContext";
import { useEffect, useState } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { SelectableAlertCard } from "./SelectableAlertCard";
import { toast } from "sonner";

interface UngroupDialogProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  onSave: (selectedIncidentIds: number[]) => Promise<void>;
}

export const UngroupDialog = ({
  open,
  onClose,
  groupId,
  onSave,
}: UngroupDialogProps) => {
  const { incidents, isLoading } = useIncidents();
  const [groupAlerts, setGroupAlerts] = useState<Incident[]>([]);
  const [selectedAlerts, setSelectedAlerts] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const filteredAlerts = incidents.filter(
      (incident) => incident.grouped && incident.groupID === groupId
    );
    setGroupAlerts(filteredAlerts);

    setSelectedAlerts(new Set(filteredAlerts.map((alert) => alert.incidentID)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, open]);

  const handleCheckboxChange = (incidentId: number) => {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(incidentId)) {
      newSelected.delete(incidentId);
    } else {
      newSelected.add(incidentId);
    }
    setSelectedAlerts(newSelected);
  };

  const handleSave = async () => {
    try {
      const unSelectedIds = groupAlerts
        .filter((alert) => !selectedAlerts.has(alert.incidentID))
        .map((alert) => alert.incidentID);

      setIsSaving(true);
      await onSave(unSelectedIds);
    } catch (error) {
      console.error("Failed to ungroup incidents:", error);
      toast.error("Failed to ungroup incidents. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[450px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="dark:text-gray-200">Ungroup Incidents</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh] space-y-2 pr-2">
          {isLoading ? (
            <div className="flex justify-center items-center py-4">
              <LoadingSpinner size="medium" color="rgb(5 83 156)" />
            </div>
          ) : (
            groupAlerts.map((alert) => (
              <SelectableAlertCard
                key={alert.incidentID}
                alert={alert}
                isSelected={selectedAlerts.has(alert.incidentID)}
                onToggle={handleCheckboxChange}
              />
            ))
          )}
        </div>
        <DialogFooter className="mt-2 gap-2">
          <Button variant="outline" className="dark:text-gray-200 dark:bg-dark" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="disabled:cursor-not-allowed"
            disabled={isSaving || selectedAlerts.size === 0}
          >
            {isSaving ? (
              <>
                <LoadingSpinner size="small" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
